/**
 * The in-app half of a GROUPED reminder notification.
 *
 * When one evaluation tick produces more than a tenant's `group_threshold`
 * reminders for the same (recipient, rule), the backend emits ONE notification and
 * ONE email instead of N — naming the first `group_max_items` entities and counting
 * the rest (`shared/reminders/grouping.py`,
 * `docs/platform/nagging-reminders-design.md` § Grouping). The per-entity
 * `reminder_states` rows are untouched; only delivery collapses.
 *
 * The bell therefore has to render one row that EXPANDS into that list, which is
 * the only surface where `notification.data` is read for anything but a URL. This
 * module is the single parser for that payload: `data` is untyped JSONB, so every
 * field is validated here rather than trusted at three call sites.
 */

import { safeNotificationPath } from './actionUrl';

export interface GroupedReminderItem {
  entityType: string;
  entityId: string;
  /** Data-only label the rule supplied — a name or reference, never prose. */
  label: string;
  /** Same-origin in-app path, or null for an entity the URL registry can't address. */
  path: string | null;
  /**
   * This entity's OWN `reminder_states` row, or null on a digest emitted before the
   * backend carried one per item.
   *
   * The flat `reminder_state_ids` beside it cannot answer this: it covers every due
   * row while `group_items` is capped at `group_max_items`, so indexing one against
   * the other pairs a contact with a stranger's reminder whenever a burst overflows —
   * which is the case grouping exists for. Null means the row's nag-scoped actions are
   * withheld, never that they act on the wrong row.
   */
  reminderStateId: string | null;
}

export interface GroupedReminder {
  /** How many reminders the emission covers — always ≥ `items.length`. */
  total: number;
  /** The named entities, oldest condition first. */
  items: GroupedReminderItem[];
  /** How many are covered but not named. */
  remaining: number;
}

function parseItem(raw: unknown): GroupedReminderItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;
  const label = typeof item.label === 'string' ? item.label : '';
  const entityId = typeof item.entity_id === 'string' ? item.entity_id : '';
  // A row with nothing to show is dropped rather than rendered blank — the "+N
  // more" count below still accounts for it, so nothing silently disappears.
  if (!label || !entityId) return null;
  return {
    entityType: typeof item.entity_type === 'string' ? item.entity_type : '',
    entityId,
    label,
    reminderStateId:
      typeof item.reminder_state_id === 'string' && item.reminder_state_id.length > 0
        ? item.reminder_state_id
        : null,
    // The path is persisted, server-supplied and handed to navigate(), so it goes
    // through the same open-redirect boundary as `action_url`.
    path: safeNotificationPath(item.path),
  };
}

/**
 * The grouped payload on a notification's `data` bag, or null when it isn't one.
 *
 * Returns null for every ordinary notification, so a caller can branch on it.
 */
export function parseGroupedReminder(data: unknown): GroupedReminder | null {
  if (!data || typeof data !== 'object') return null;
  const bag = data as Record<string, unknown>;
  if (bag.grouped !== true) return null;

  const rawItems = Array.isArray(bag.group_items) ? bag.group_items : [];
  const items = rawItems
    .map(parseItem)
    .filter((item): item is GroupedReminderItem => item !== null);
  if (items.length === 0) return null;

  const count = typeof bag.group_count === 'number' ? bag.group_count : 0;
  // The count is authoritative for "how many are waiting" but must never make the
  // remainder negative if a payload ever disagrees with its own list.
  const total = Math.max(count, items.length);
  return { total, items, remaining: total - items.length };
}
